/**
 * Financial Breakdown Panel — Clean, borderless, Apple-level breakdown rows.
 * Expands inline with smooth animation. Each row has a "Breakdown →" affordance.
 */
import { useState, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import type { MaskedForecast, CostEstimate, RevenueLine } from "@/data/fie/types";
import type { FinancialAttribution } from "@/data/fie/attributionTypes";
import { cn } from "@/lib/utils";

function fmt(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value);
}

interface Props {
  forecast: MaskedForecast;
  canViewCosts: boolean;
  canViewEconomics: boolean;
  onOpenLedger: (attributions: FinancialAttribution[], title: string, subtitle: string) => void;
}

export function BreakdownPanel({ forecast: f, canViewCosts, canViewEconomics, onOpenLedger }: Props) {
  return (
    <div className="animate-in slide-in-from-top-2 duration-300 ease-out">
      {/* Revenue section */}
      {f.revenue && (
        <BreakdownSection title="Revenue" total={f.revenue.expected}>
          {f.revenueLines?.map(r => (
            <BreakdownRow
              key={r.jobId}
              label={r.customerName}
              sublabel={r.projectType}
              amount={r.contractValue}
              isActual={r.sourceType === "actual"}
              onDrillDown={r.attributions.length > 0
                ? () => onOpenLedger(r.attributions, r.customerName, `${r.projectType} · ${r.jobId}`)
                : undefined
              }
            />
          ))}
        </BreakdownSection>
      )}

      {/* Costs section */}
      {f.costs && canViewCosts && (
        <BreakdownSection title="Costs" total={f.totalCosts?.expected || 0}>
          {f.costs.map(c => (
            <BreakdownRow
              key={c.category}
              label={c.category}
              sublabel={c.sourceType === "actual" ? "Verified" : c.sourceType === "blended" ? "Blended" : "Estimated"}
              amount={c.blended}
              isActual={c.sourceType === "actual"}
              onDrillDown={c.attributions.length > 0
                ? () => onOpenLedger(c.attributions, `${c.category.charAt(0).toUpperCase() + c.category.slice(1)}`, fmt(c.blended))
                : undefined
              }
            />
          ))}
        </BreakdownSection>
      )}

      {/* Profit */}
      {f.netProfit && canViewEconomics && (
        <div className="flex items-center justify-between py-4 mt-2 border-t border-border/30">
          <p className="text-[15px] font-semibold text-foreground">Net Profit</p>
          <p className="text-[18px] font-bold tabular-nums tracking-tight">{fmt(f.netProfit.expected)}</p>
        </div>
      )}

      {/* Distributable */}
      {f.distributableAmount && canViewEconomics && (
        <div className="flex items-center justify-between py-3">
          <p className="text-[13px] text-muted-foreground">Distributable Amount</p>
          <p className="text-[15px] font-semibold tabular-nums">{fmt(f.distributableAmount.expected)}</p>
        </div>
      )}
    </div>
  );
}

// ── Section ──
function BreakdownSection({ title, total, children }: {
  title: string; total: number; children: React.ReactNode;
}) {
  return (
    <div className="mb-6">
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">{title}</p>
        <p className="text-[13px] font-semibold tabular-nums text-muted-foreground">{fmt(total)}</p>
      </div>
      <div className="space-y-0">{children}</div>
    </div>
  );
}

// ── Single Row ──
function BreakdownRow({ label, sublabel, amount, isActual, onDrillDown }: {
  label: string; sublabel?: string; amount: number; isActual: boolean;
  onDrillDown?: () => void;
}) {
  return (
    <button
      onClick={onDrillDown}
      disabled={!onDrillDown}
      className={cn(
        "w-full flex items-center gap-3 py-3 px-3 -mx-3 rounded-lg transition-all duration-150 group text-left",
        onDrillDown ? "hover:bg-muted/40 cursor-pointer" : "cursor-default"
      )}
    >
      {/* Status dot */}
      <div className={cn(
        "h-[6px] w-[6px] rounded-full shrink-0",
        isActual ? "bg-success/60" : "bg-warning/60"
      )} />

      {/* Label */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium capitalize truncate">{label}</p>
        {sublabel && (
          <p className="text-[11px] text-muted-foreground/60 truncate">{sublabel}</p>
        )}
      </div>

      {/* Amount */}
      <p className="text-[14px] font-semibold tabular-nums shrink-0">{fmt(amount)}</p>

      {/* Drill affordance */}
      {onDrillDown && (
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors shrink-0" />
      )}
    </button>
  );
}
